import { styled } from "@mui/material/styles";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import { useStyles } from "../../common/appStyles";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#10366E",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
  },
}));

const StyledTableCellReport = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#10366E",
    color: theme.palette.common.white,
    padding: "6px 6px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 9,
    padding: "6px 1px",
  },
}));

export default function TableComponent({
  results,
  firstColumnTitle,
  columnTitles,
  pagination,
  noPadding,
  fixedSizeInPx,
  greaterLetterSize,
  whiteSpaceValue,
  reportSize,
  notNumbered,
  customHeight,
}) {
  const classes = useStyles();

  const hasActionsColumn = !!firstColumnTitle;

  const renderTable = () => (
    <TableContainer component={Paper} sx={{ borderRadius: "4px" }}>
      <Table stickyHeader size="small">
        <TableHead sx={{ whiteSpace: whiteSpaceValue ?? "nowrap" }}>
          <StyledTableRow key={0}>
            {!notNumbered &&
              (reportSize ? (
                <StyledTableCellReport
                  key="title-cell-number"
                  className={
                    reportSize
                      ? classes.tableHeaderReport
                      : classes.tableHeader
                  }
                >
                  {"#"}
                </StyledTableCellReport>
              ) : (
                <StyledTableCellReport
                  key="title-cell-number"
                  className={
                    reportSize
                      ? classes.tableHeaderReport
                      : classes.tableHeader
                  }
                >
                  {"#"}
                </StyledTableCellReport>
              ))}

            {firstColumnTitle &&
              (reportSize ? (
                <StyledTableCell
                  key="title-cell-first"
                  className={
                    reportSize
                      ? classes.tableHeaderReport
                      : classes.tableHeader
                  }
                >
                  {firstColumnTitle}
                </StyledTableCell>
              ) : (
                <StyledTableCellReport
                  key="title-cell-first"
                  className={
                    reportSize
                      ? classes.tableHeaderReport
                      : classes.tableHeader
                  }
                >
                  {firstColumnTitle}
                </StyledTableCellReport>
              ))}
            {columnTitles.map((title) =>
              reportSize ? (
                <StyledTableCell
                  key={`title-${title}`}
                  className={
                    reportSize
                      ? classes.tableHeaderReport
                      : classes.tableHeader
                  }
                >
                  {title}
                </StyledTableCell>
              ) : (
                <StyledTableCell
                  key={`title-${title}`}
                  className={
                    reportSize
                      ? classes.tableHeaderReport
                      : classes.tableHeader
                  }
                >
                  {title}
                </StyledTableCell>
              )
            )}
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {results?.map((row, index) => (
            <StyledTableRow key={row.key}>
              {!notNumbered &&
                (reportSize ? (
                  <StyledTableCellReport
                    key={`${row.key}-cell-num`}
                    className={
                      greaterLetterSize
                        ? classes.tableRowLarger
                        : classes.tableRow
                    }
                    sx={{
                      whiteSpace: whiteSpaceValue ?? "nowrap",
                      width: "30px",
                    }}
                  >
                    {index + 1}
                  </StyledTableCellReport>
                ) : (
                  <StyledTableCell
                    key={`${row.key}-cell-num`}
                    className={
                      reportSize ? classes.tableRowLarger : classes.tableRow
                    }
                    sx={{ width: "30px" }}
                  >
                    {index + 1}
                  </StyledTableCell>
                ))}

              {row.data.map((data, colIndex) =>
                reportSize ? (
                  <StyledTableCellReport
                    key={`${row.key}-cell-${colIndex}`}
                    className={
                      greaterLetterSize
                        ? classes.tableRowLarger
                        : classes.tableRow
                    }
                    sx={{
                      whiteSpace: whiteSpaceValue ?? "nowrap",
                    }}
                  >
                    {data}
                  </StyledTableCellReport>
                ) : (
                  <StyledTableCell
                    key={`${row.key}-cell-${colIndex}`}
                    className={
                      greaterLetterSize
                        ? classes.tableRowLarger
                        : classes.tableRow
                    }
                    sx={{
                      whiteSpace: whiteSpaceValue ?? "nowrap",
                    }}
                  >
                    {data}
                  </StyledTableCell>
                )
              )}
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderCards = () => (
    <div className="w-full flex flex-col gap-2">
      {results?.map((row, index) => {
        const actionCell = hasActionsColumn ? row.data[0] : null;
        const valueCells = hasActionsColumn ? row.data.slice(1) : row.data;
        return (
          <div
            key={row.key ?? index}
            className="flex flex-row w-full rounded overflow-hidden bg-white"
            style={{
              border: "1px solid #E5E7EB",
              boxShadow:
                "rgba(60, 64, 67, 0.15) 0px 1px 2px 0px, rgba(60, 64, 67, 0.1) 0px 1px 3px 1px",
            }}
          >
            {hasActionsColumn && (
              <div
                className="flex flex-col items-center justify-start gap-2 p-2 shrink-0"
                style={{ backgroundColor: "#EEF2F7", borderRight: "1px solid #E5E7EB" }}
              >
                {!notNumbered && (
                  <span className="text-xs text-gray-500">{`#${index + 1}`}</span>
                )}
                {actionCell}
              </div>
            )}
            <div className="flex flex-col flex-1 min-w-0 p-2">
              {!hasActionsColumn && !notNumbered && (
                <div className="flex items-center justify-between gap-2 py-1 border-b border-gray-100">
                  <span className="text-xs font-semibold text-gray-600 shrink-0">
                    {"#"}
                  </span>
                  <span className="text-xs text-right min-w-0" style={{ wordBreak: "break-word" }}>
                    {index + 1}
                  </span>
                </div>
              )}
              {columnTitles.map((title, i) => (
                <div
                  key={`${row.key}-card-${i}`}
                  className="flex items-center justify-between gap-2 py-1 border-b border-gray-100 last:border-b-0"
                >
                  <span className="text-xs font-semibold text-gray-600 shrink-0">
                    {title}
                  </span>
                  <span
                    className="text-xs text-right min-w-0"
                    style={{ wordBreak: "break-word" }}
                  >
                    {valueCells[i]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      className={`flex justify-center items-center overflow-x-auto flex-col ${
        noPadding ? "" : "lateral_padding"
      }`}
    >
      <div
        className=" w-full p-1"
        style={
          customHeight
            ? { height: customHeight }
            : fixedSizeInPx
            ? { height: `${fixedSizeInPx}px` }
            : {}
        }
      >
        {reportSize ? (
          renderTable()
        ) : (
          <>
            <div className="hidden md:block w-full">{renderTable()}</div>
            <div className="block md:hidden w-full">{renderCards()}</div>
          </>
        )}
      </div>
      {pagination}
    </div>
  );
}
